import type { CompanyResponse } from '@shared/schemas';

import type { CountryData } from '@/components/dashboard/WorldMap';
import { initialsOf } from '@/shared/ui/initials';
import { resolveCountryCode } from '@/utils/countryMap';
import { countryName } from '@/utils/format';

import type { LinkedInLocation, TeamMember } from './company-detail.model';

/**
 * Pure derivations for the Company Detail page. Everything the original component
 * computed inline (locations parsing, specialties, the world-map aggregation, data
 * freshness, team split) lives here as framework-free functions, so each rule is
 * unit-testable and the views/selectors just call them.
 */

/** Enrichment is normally skipped for data younger than this many days. */
export const STALE_DAYS = 30;

const MS_PER_DAY = 86_400_000;

/** `company.locations` is loose JSON; coerce it to a typed array (or empty). */
export function parseLocations(company: CompanyResponse | null): LinkedInLocation[] {
  return Array.isArray(company?.locations) ? (company.locations as LinkedInLocation[]) : [];
}

/** Locations that are not the headquarter (the "other offices" list). */
export function secondaryLocations(company: CompanyResponse | null): LinkedInLocation[] {
  return parseLocations(company).filter((l) => !l.is_headquarter);
}

/** Comma-separated specialties → trimmed, non-empty chips. */
export function parseSpecialties(company: CompanyResponse | null): string[] {
  if (!company?.specialties) return [];
  return company.specialties
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Whether any headquarter field is populated. */
export function hasHeadquarters(company: CompanyResponse | null): boolean {
  return Boolean(company?.hq_city || company?.hq_country || company?.hq_full_address);
}

/** HQ (0 or 1) + secondary office count; drives the Location tab badge. */
export function locationCount(company: CompanyResponse | null): number {
  return (hasHeadquarters(company) ? 1 : 0) + secondaryLocations(company).length;
}

/* --------------------------------------------------------------------------
 * Addresses
 *
 * `full_address` is not a postal address: it is one anglo template
 * (`line1, [city,] [region ]zip, COUNTRY`) that LinkedIn applies to every
 * country alike. A Dutch office comes back as "Gustav Mahlerlaan 10, Amsterdam
 * 1082PP, NL" where the local form is "Gustav Mahlerlaan 10 / 1082 PP
 * Amsterdam"; a Belgian one carries a region no Belgian address ever prints.
 *
 * Every part that template concatenates is also stored on its own, so whenever
 * the street is there we rebuild the address for its country and keep
 * `full_address` only as the fallback for rows that carry nothing else. That
 * also ends the duplication the tab used to show, where the street and the
 * postcode were printed once inside `full_address` and once again underneath.
 *
 * The country is deliberately absent from the lines: a flag sits beside them
 * everywhere they render, and a 380px column has no room to say it twice.
 * -------------------------------------------------------------------------- */

interface AddressParts {
  city: string;
  region: string;
  zip: string;
}

/** How city, region and postcode line up below the street, per country. */
type LocalityFormat = (p: AddressParts) => string[];

/** Postcode then city, one line, no region; most of continental Europe. */
const ZIP_BEFORE_CITY: LocalityFormat = (p) => [[p.zip, p.city].filter(Boolean).join(' ')];

/** City, then the postcode alone on the last line; UK and Ireland. */
const ZIP_ON_ITS_OWN_LINE: LocalityFormat = (p) => [p.city, p.zip];

/** "Palo Alto, CA 94301"; the state belongs to the address here. */
const CITY_STATE_ZIP: LocalityFormat = (p) => [
  [[p.city, p.region].filter(Boolean).join(', '), p.zip].filter(Boolean).join(' '),
];

/** No rule for this country: name every part we hold, postcode last. */
const GENERIC: LocalityFormat = (p) => [[p.city, p.region].filter(Boolean).join(', '), p.zip];

const FORMATS: Record<string, LocalityFormat> = {
  AT: ZIP_BEFORE_CITY,
  BE: ZIP_BEFORE_CITY,
  CH: ZIP_BEFORE_CITY,
  CZ: ZIP_BEFORE_CITY,
  DE: ZIP_BEFORE_CITY,
  DK: ZIP_BEFORE_CITY,
  ES: ZIP_BEFORE_CITY,
  FI: ZIP_BEFORE_CITY,
  FR: ZIP_BEFORE_CITY,
  IT: ZIP_BEFORE_CITY,
  LU: ZIP_BEFORE_CITY,
  NL: ZIP_BEFORE_CITY,
  NO: ZIP_BEFORE_CITY,
  PL: ZIP_BEFORE_CITY,
  PT: ZIP_BEFORE_CITY,
  SE: ZIP_BEFORE_CITY,
  GB: ZIP_ON_ITS_OWN_LINE,
  IE: ZIP_ON_ITS_OWN_LINE,
  AU: CITY_STATE_ZIP,
  CA: CITY_STATE_ZIP,
  US: CITY_STATE_ZIP,
};

/**
 * LinkedIn does not normalise postcodes; the same country arrives as "1082PP"
 * and as "5211 NL". Only the two shapes with a mandatory internal space are
 * repaired; anything else is passed through, because guessing at a format we
 * have no rule for is how a valid postcode gets broken.
 */
function normalizeZip(zip: string, code: string | null): string {
  if (!zip) return '';
  const raw = zip.replace(/\s+/g, ' ').trim().toUpperCase();
  if (code === 'NL') {
    const m = /^(\d{4})\s?([A-Z]{2})$/.exec(raw);
    return m ? `${m[1]} ${m[2]}` : raw;
  }
  if (code === 'GB') {
    const m = /^([A-Z]{1,2}\d[A-Z\d]?)\s?(\d[A-Z]{2})$/.exec(raw);
    return m ? `${m[1]} ${m[2]}` : raw;
  }
  return raw;
}

const trimmed = (value?: string | null): string => value?.trim() ?? '';

/**
 * An address as the lines it should be printed on, in its own country's order.
 *
 * Empty when nothing is usable; a row reading "Unknown" still claims we hold
 * an office there, which is the opposite of what an empty record means.
 */
export function formatAddressLines(loc: LinkedInLocation | null): string[] {
  if (!loc) return [];
  const code = loc.country ? resolveCountryCode(loc.country) : null;
  const street = [loc.line1, loc.line2].map(trimmed).filter(Boolean).join(', ');

  if (street) {
    const parts: AddressParts = {
      city: trimmed(loc.city),
      region: trimmed(loc.region),
      zip: normalizeZip(trimmed(loc.zipcode), code),
    };
    const format = (code && FORMATS[code]) || GENERIC;
    return [street, ...format(parts)].map((l) => l.trim()).filter(Boolean);
  }

  // No street to rebuild from. Reformatting `full_address` would mean parsing
  // free text, which is how addresses get mangled; so it is printed as is.
  const full = trimmed(loc.full_address);
  if (full) return [full];

  // A city and no more: the country is spelled out here, because a row this
  // coarse is a place rather than an address and the name is what reads.
  const coarse = [trimmed(loc.city), trimmed(loc.region), loc.country ? countryName(loc.country) : '']
    .filter(Boolean)
    .join(', ');
  return coarse ? [coarse] : [];
}

/** The formatted address on one line; for a map query or a link's name. */
export function locationLabel(loc: LinkedInLocation): string | null {
  const lines = formatAddressLines(loc);
  return lines.length > 0 ? lines.join(', ') : null;
}

/**
 * The headquarters as one address.
 *
 * The `hq_*` columns hold no street field (only `hq_full_address` carries one,
 * baked into LinkedIn's template) but the headquarters is also one of the
 * `locations` rows, flagged, and that row is fully structured. Read the flagged
 * row first and let the columns fill whatever it leaves out, so the HQ formats
 * by country like every other office.
 */
export function headquartersAddress(company: CompanyResponse | null): LinkedInLocation | null {
  if (!hasHeadquarters(company)) return null;
  const flagged = parseLocations(company).find((l) => l.is_headquarter);
  return {
    line1: flagged?.line1,
    line2: flagged?.line2,
    city: flagged?.city || company?.hq_city || undefined,
    region: flagged?.region || company?.hq_region || undefined,
    zipcode: flagged?.zipcode || company?.hq_postal_code || undefined,
    country: flagged?.country || company?.hq_country || undefined,
    full_address: flagged?.full_address || company?.hq_full_address || undefined,
  };
}

/** The headquarters address ready to print, empty when there is none. */
export function headquartersLines(company: CompanyResponse | null): string[] {
  return formatAddressLines(headquartersAddress(company));
}

/**
 * Below this many offices, a table costs more than it carries.
 *
 * One row under a toolbar, a header and a "1–1 of 1" footer is chrome around a
 * single address; and one office is the common case, not the edge one. The
 * threshold is the count, so the page changes shape exactly when there is
 * something to sort, search or paginate.
 */
export const OFFICE_TABLE_MIN_ROWS = 2;

/** One office, as a table row. */
export interface OfficeRow {
  /** Stable within a render: the table needs a key, offices carry no id. */
  id: string;
  city: string | null;
  country: string | null;
  /** The formatted address on one line; what the table column shows. */
  address: string | null;
  /** The same address as the lines it should be printed on; what a card shows. */
  lines: string[];
  isHQ: boolean;
}

/**
 * Every office as one flat list, the headquarters first.
 *
 * One list rather than "the HQ" and "the others": the table sorts, and a
 * headquarters pulled out into its own box stops being sortable with the rest.
 * It keeps its rank through `isHQ`, which the row treatment reads; a marker
 * that survives a sort, where a position cannot.
 *
 * The HQ row goes through `headquartersAddress`, so it is formatted by the same
 * country rules as every other office rather than printed from the columns as a
 * special case. `secondaryLocations` already drops the flagged entry, so nothing
 * is counted twice.
 */
export function officeRows(company: CompanyResponse | null): OfficeRow[] {
  const rows: OfficeRow[] = [];

  const hq = headquartersAddress(company);
  if (hq) {
    const lines = formatAddressLines(hq);
    rows.push({
      id: 'hq',
      city: trimmed(hq.city) || null,
      country: trimmed(hq.country) || null,
      address: lines.length > 0 ? lines.join(', ') : null,
      lines,
      isHQ: true,
    });
  }

  secondaryLocations(company).forEach((loc, i) => {
    const lines = formatAddressLines(loc);
    // Nothing readable at all: dropped rather than kept as a blank row, which
    // would still claim we hold an office there.
    if (lines.length === 0 && !trimmed(loc.city) && !trimmed(loc.country)) return;
    rows.push({
      id: `office-${i}`,
      city: trimmed(loc.city) || null,
      country: trimmed(loc.country) || null,
      address: lines.length > 0 ? lines.join(', ') : null,
      lines,
      isHQ: false,
    });
  });

  return rows;
}

/** Aggregate all locations by country for the world map, flagging the HQ country. */
export function buildMapData(company: CompanyResponse | null): CountryData[] {
  const items: CountryData[] = [];
  const seen = new Set<string>();
  if (company?.hq_country) {
    items.push({ country: company.hq_country, count: 1, isHQ: true });
    seen.add(company.hq_country);
  }
  const officeCountMap: Record<string, number> = {};
  for (const loc of secondaryLocations(company)) {
    if (loc.country && !seen.has(loc.country)) {
      officeCountMap[loc.country] = (officeCountMap[loc.country] || 0) + 1;
    }
  }
  for (const [country, count] of Object.entries(officeCountMap)) {
    items.push({ country, count });
  }
  return items;
}

/** Age of the company data in whole days at `nowMs`, or null if never updated. */
export function dataAgeInDays(company: CompanyResponse | null, nowMs: number): number | null {
  if (!company?.data_updated_at) return null;
  return Math.floor((nowMs - new Date(company.data_updated_at).getTime()) / MS_PER_DAY);
}

/** Data is "fresh" (enrichment would normally be skipped) when younger than STALE_DAYS. */
export function isFreshData(ageDays: number | null): boolean {
  return ageDays !== null && ageDays < STALE_DAYS;
}

export interface TeamSplit {
  current: TeamMember[];
  alumni: TeamMember[];
}

/** Partition the team into current members and alumni. */
export function splitTeam(team: TeamMember[]): TeamSplit {
  return {
    current: team.filter((m) => m.is_current),
    alumni: team.filter((m) => !m.is_current),
  };
}

/* --------------------------------------------------------------------------
 * Profiles
 * -------------------------------------------------------------------------- */

/**
 * Below this many profiles, a table costs more than it carries; same reasoning
 * and same threshold as the offices.
 */
export const PROFILE_TABLE_MIN_ROWS = 2;

/** One tracked profile, as a table row. */
export interface ProfileRow {
  id: string;
  name: string;
  /** Avatar fallback when there is no picture. */
  initials: string;
  imageUrl: string | null;
  /** The title held *at this company*, not the profile's current one. */
  title: string | null;
  city: string | null;
  country: string | null;
  /** "2016 – 2025", or "2020 – now" while the experience is live. */
  period: string | null;
  isCurrent: boolean;
  linkedinUrl: string | null;
}

/**
 * The years spent at this company, as a range.
 *
 * Years only, because that is all `ProfileExperience` stores; no month ever
 * reaches here, so none is ever implied. An experience flagged current ends at
 * "now" rather than at this year: the record says it has not ended, and writing
 * the current year would state an end nobody claimed.
 */
export function experiencePeriod(
  startYear: number | null,
  endYear: number | null,
  isCurrent: boolean,
): string | null {
  if (startYear === null) return isCurrent ? 'Now' : null;
  const end = isCurrent ? 'now' : (endYear ?? '?');
  return `${startYear} – ${end}`;
}

/**
 * The tracked profiles as table rows.
 *
 * These are the profiles Northstar holds who have an experience at this
 * company; not the company's staff, which is a LinkedIn headcount shown a few
 * centimetres above. The distinction is stated in the tab, and it is the reason
 * `title` is the title held *here* rather than the profile's current one.
 */
export function profileRows(team: TeamMember[]): ProfileRow[] {
  return team.map((m) => ({
    id: m.id,
    name: trimmed(m.full_name) || 'Unnamed profile',
    initials: initialsOf(m.first_name ?? '', m.last_name ?? ''),
    imageUrl: trimmed(m.profile_image_url) || null,
    title: trimmed(m.title) || trimmed(m.job_title) || null,
    city: trimmed(m.city) || null,
    country: trimmed(m.country) || null,
    period: experiencePeriod(m.start_year, m.end_year, m.is_current),
    isCurrent: m.is_current,
    linkedinUrl: trimmed(m.linkedin_url) || null,
  }));
}
